<template>
    <span style="display: none;"/>
</template>

<script>
    import {mapActions, mapGetters} from 'vuex';
    import {getStoredMusicVolume, getStoredButtonVolume} from '@/utils/volumeStorage';

    export default {
        name: 'MusicPlayer',
        components: {},
        data() {
            return {
                songFiles: [],
                // Lazy cache Audio objektu, klic = index v songFiles (viz getAudio() nize).
                audioCache: {},
                actualTrack: null,
                playlistCategories: null,
                actualCategory: {type: Object, default: null},
            }
        },
        watch: {
            track() {
                this.playNewMusic();
            },
            musicVolume() {
                if (this.actualTrack) {
                    this.actualTrack.volume = this.musicVolume / 100;
                    // iOS Safari ignoruje .volume (setter je no-op, viz getAudio() nize) - bez .muted by na iPhone hudba hrala porad na plno.
                    this.actualTrack.muted = this.musicVolume === 0;
                }
            },
        },
        computed: {
            ...mapGetters({
                track: 'journey/track',
                musicVolume: 'journey/musicVolume',
                user: 'user/item',
            }),
        },
        created() {
            // Jen jmena souboru, zadne Audio() instance tady (viz lazy getAudio() nize) - drive se vytvorilo vsech 32 Audio objektu najednou pri kazdem startu appky (81 MB na pozadi bez ohledu na pouziti).
            this.songFiles = [
                'Catch.mp3',               //  0
                'Dungeon common.mp3',      //  1
                'Dungeon.mp3',             //  2
                'Fight dark.mp3',          //  3
                'Fight epic.mp3',          //  4
                'Fight low.mp3',           //  5
                'Fight low2.mp3',          //  6
                'Fight newtwo.mp3',        //  7 --- zatim nepouzito
                'Fight normal.mp3',        //  8
                'Fight waitin for.mp3',    //  9
                'Fight.mp3',               //  10
                'League lost.mp3',         //  11
                'League start.mp3',        //  12
                'League win.mp3',          //  13
                'Legend.mp3',              //  14 --- zatim nepouzito
                'Pohoda cesta.mp3',        //  15
                'Pohoda cirkus.mp3',       //  16
                'Pohoda deprese.mp3',      //  17
                'Pohoda louka.mp3',        //  18
                'Pohoda pianko.mp3',       //  19
                'Pohoda plaz.mp3',         //  20
                'Pohoda vesnice.mp3',      //  21
                'Pohoda voda.mp3',         //  22
                'Problems.mp3',            //  23
                'Ruins.mp3',               //  24  --- zatim nepouzito
                'Stadion trainer win.mp3', //  25
                'Start.mp3',               //  26
                'Team Rocket.mp3',         //  27
                'Wild trainer win.mp3',    //  28
                'Pohoda route 29.mp3',     //  29
                'Pohoda kolo.mp3',         //  30
                'Pohoda surfing.mp3',      //  31
            ];
            this.playlistCategories = {
                start:  {list: [26], lastSong: null},
                travel:  {list: [15,18,19,20,22,29,30], lastSong: null},
                city:  {list: [15,16,17,21,22,31], lastSong: null},
                fight:  {list: [8,10], lastSong: null},
                fight_low:   {list: [5,6], lastSong: null},
                fight_trainer_stadion_win:  {list: [25], lastSong: null},
                mewtwo_fight_win:  {list: [25], lastSong: null},
                fight_trainer_wild_win:  {list: [28], lastSong: null},
                team_rocket:  {list: [27], lastSong: null},
                dungeon:  {list: [1], lastSong: null},
                dungeon_challenge:  {list: [2], lastSong: null},
                fight_challenge_boss:  {list: [3], lastSong: null},
                fight_dungeon_boss:  {list: [23], lastSong: null},
                dungeon_win:  {list: [0], lastSong: null},
                league_start:  {list: [12], lastSong: null},
                wait_for_fight:  {list: [9], lastSong: null},
                league_fight:  {list: [26], lastSong: null},
                league_fight_epic:  {list: [4], lastSong: null},
                league_victory:  {list: [13], lastSong: null},
                league_lose:  {list: [11], lastSong: null},
            }
            // Lokalni (per-zarizeni) hodnota ma prednost pred serverovou - viz volumeStorage.js. Serverova hodnota je fallback jen pro zarizeni, ktere jeste zadnou svou lokalni hodnotu nema.
            this.musicVolumeSetup(getStoredMusicVolume(this.user.id, this.user.musicVolume));
            this.buttonsVolumeSetup(getStoredButtonVolume(this.user.id, this.user.buttonVolume));
            this.playNewMusic();
        },
        beforeDestroy() {
            // bez tohohle by po odmountovani (prepnuti newUi flagu za behu)
            // hrala hudba dal jako "duch" a ended-listener by spoustel dalsi tracky
            if (this.actualTrack) {
                this.actualTrack.removeEventListener('ended', this.nextTrack, true);
                this.actualTrack.pause();
            }
        },
        methods: {
            ...mapActions({
                buttonsVolumeSetup: 'journey/buttonsVolumeSetup',
                musicVolumeSetup: 'journey/musicVolumeSetup',
            }),
            getAudio(index) {
                // Lazy vytvoreni + cache podle indexu v songFiles - kazdy track se stahuje az kdyz ho hra poprve potrebuje (vytvari se hned pri zmene track kategorie, tedy jeste pred prekreslenim obrazovky). Service worker ("music-cache") navic zajisti, ze dalsi prehrani jde z lokalni cache bez site.
                if (!this.audioCache[index]) {
                    let backgroundSound = require('@/assets/music/'+this.songFiles[index]);
                    this.audioCache[index] = new Audio(backgroundSound);
                    // iOS Safari ma .volume setter no-op (hlasitost jde nastavit jen hardwarovymi tlacitky) - bez .muted by nova skladba na iPhone chvilku hrala nahlas i pri vypnutem zvuku.
                    this.audioCache[index].muted = this.musicVolume === 0;
                }
                return this.audioCache[index];
            },
            async playNewMusic(push = false) {
                if (this.track === 'none') {
                    return;
                }
                if (this.actualTrack) {
                    this.actualTrack.pause();
                }
                this.actualCategory = this.playlistCategories[this.track.replace(/-/g, "_")];
                if(! this.actualCategory) {
                    this.actualCategory = this.playlistCategories.city;
                }
                let songIndex;
                if (this.actualCategory['lastSong'] === null) {
                    let random = Math.floor(Math.random() * this.actualCategory.list.length);
                    songIndex = this.actualCategory.list[random];
                } else {
                    songIndex = this.actualCategory.lastSong;
                }
                this.actualTrack = this.getAudio(songIndex);
                // Cachovana instance mohla byt naposledy pouzita s jinym stavem hlasitosti - dorovnat pred play().
                this.actualTrack.muted = this.musicVolume === 0;
                this.actualTrack.addEventListener('ended', this.nextTrack, true);
                if (this.actualTrack) {
                   if (push) {
                       this.actualTrack.time = 0;
                   }
                  try {
                    await this.actualTrack.play();
                    this.fadeIn();
                  } catch (error) {
                    console.warn('Autoplay byl zablokován prohlížečem. Hudba se spustí po první interakci.', error.name);
                    this.waitForUserInteraction();
                  }

                   this.actualCategory.lastSong = songIndex;
                }
            },
            waitForUserInteraction() {
              const playOnClick = () => {
                if (this.actualTrack && this.actualTrack.paused) {
                  this.actualTrack.play().then(() => {
                    this.fadeIn();
                  }).catch(e => {
                    console.error('Přehrávání selhalo i po kliknutí:', e);
                  });
                }
                window.removeEventListener('click', playOnClick);
                window.removeEventListener('keydown', playOnClick);
              };

              window.addEventListener('click', playOnClick, { once: true });
              window.addEventListener('keydown', playOnClick, { once: true });
            },
            nextTrack() {
                this.actualTrack.time = 0;
                this.actualTrack.pause();
                this.actualCategory.lastSong = null;
                this.playNewMusic(true);
            },
            fadeOut() {
                let backgroundAudio = this.actualTrack;
                let actualVolumeFadeOut = this.actualTrack.volume;
                let tickAmount = 0.05;
                let interval = 200;
                    let fadeOutInterval = setInterval(function() {
                        actualVolumeFadeOut = (parseFloat(actualVolumeFadeOut) - tickAmount).toFixed(2);
                        if (actualVolumeFadeOut >= 0) {
                            backgroundAudio.volume = actualVolumeFadeOut;
                        } else {
                            backgroundAudio.pause();
                            clearInterval(fadeOutInterval);
                        }
                    }, interval);
            },
            async fadeIn() {
                const delay = ms => new Promise(res => setTimeout(res, ms));
                this.actualTrack.volume = 0;
                for (let i = 1; i < (this.musicVolume*10/50); i++) {
                    await delay(80);
                    this.actualTrack.volume += this.musicVolume / 2000;
                }
                if(this.actualTrack.volume) {
                    this.actualTrack.volume = this.musicVolume / 100;
                }
            },
        },
    }
</script>
